import type { GroupDefinition } from '../../types/intersection';

interface MapGroupsDiagramProps {
  groups: GroupDefinition[];
  phases: { id: string; duration: number }[];
  currentPhase: string;
}

const colorStyles: Record<string, React.CSSProperties> = {
  V: { backgroundColor: 'lawngreen' },
  A: { backgroundColor: 'gold' },
  R: { backgroundColor: '#ef4444' },
  P: { backgroundColor: 'lawngreen' },
  H: { backgroundColor: '#ef4444' },
  I: {
    backgroundImage: 'linear-gradient(90deg, lawngreen 50%, transparent 50%)',
    backgroundSize: '6px 100%',
  },
  D: { backgroundColor: '#1a1a1a' },
  N: { backgroundColor: '#1a1a1a' },
  S: {
    backgroundImage: 'linear-gradient(90deg, lawngreen 50%, transparent 50%)',
    backgroundSize: '6px 100%',
  },
  C: {
    backgroundImage: 'linear-gradient(90deg, gold 50%, transparent 50%)',
    backgroundSize: '6px 100%',
  },
  F: {
    backgroundImage: 'linear-gradient(90deg, #ef4444 50%, transparent 50%)',
    backgroundSize: '6px 100%',
  },
  B: { background: 'linear-gradient(180deg, #ef4444 50%, gold 50%)' },
};

const typeLabel: Record<string, string> = { V: 'V', P: 'P', S: 'S' };

// Group index boundaries for visual separators
const separatorAfter = new Set([4, 8, 12]); // after G4, G8, G12

function truncate(s: string, max: number) {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

export function MapGroupsDiagram({ groups, phases, currentPhase }: MapGroupsDiagramProps) {
  return (
    <div className="p-2 font-mono text-[9px] select-none">
      {/* Header row */}
      <div className="flex items-center mb-1">
        <div className="w-[100px] shrink-0" />
        {phases.map((ph) => (
          <div
            key={ph.id}
            className={`w-[28px] text-center shrink-0 ${
              ph.id === currentPhase ? 'text-white font-bold' : 'text-gray-500'
            }`}
          >
            {ph.id}
            <span className="text-[7px] text-gray-600 block">
              {(ph.duration / 10).toFixed(0)}s
            </span>
          </div>
        ))}
      </div>

      {/* Group rows */}
      {groups.map((group) => (
        <div key={group.id}>
          <div className="flex items-center h-[18px]">
            {/* Label column */}
            <div className="w-[100px] shrink-0 flex items-center gap-1 pr-1">
              <span className="text-gray-500 w-[18px] text-right">G{group.id}</span>
              <span className="text-gray-600 text-[7px]">{typeLabel[group.type]}</span>
              <span className="text-gray-400 truncate text-[8px]">
                {truncate(group.label, 12)}
              </span>
            </div>

            {/* Phase cells */}
            {phases.map((ph) => {
              const color = group.phaseColors[ph.id] || 'D';
              const isActive = ph.id === currentPhase;
              return (
                <div key={ph.id} className="w-[28px] flex justify-center shrink-0">
                  <div
                    className={`w-[16px] h-[14px] rounded-sm ${
                      isActive
                        ? 'ring-1 ring-white/60 opacity-100'
                        : 'opacity-40'
                    }`}
                    style={colorStyles[color] || { backgroundColor: '#333' }}
                  />
                </div>
              );
            })}
          </div>

          {/* Separator between logical blocks */}
          {separatorAfter.has(group.id) && (
            <div className="border-t border-gray-600/30 my-0.5" />
          )}
        </div>
      ))}
    </div>
  );
}
